import { Link } from "react-router";

interface WorkflowCardProps {
  to: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

function WorkflowCard({ to, title, description, icon }: WorkflowCardProps) {
  return (
    <Link
      to={to}
      className="group border-wg-03 hover:border-jj-red flex flex-col gap-4 rounded-2xl border bg-white p-8 transition-all hover:-translate-y-0.5 hover:shadow-lg"
    >
      <div className="text-jj-red h-10 w-10">{icon}</div>
      <h2 className="font-heading text-wg-08 text-xl font-semibold">{title}</h2>
      <p className="text-wg-06 text-sm leading-relaxed">{description}</p>
      <span className="text-jj-red mt-auto inline-flex items-center gap-2 text-sm font-semibold">
        Open workflow
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-4 w-4 transition-transform group-hover:translate-x-1"
        >
          <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
      </span>
    </Link>
  );
}

export function HomePage() {
  return (
    <>
      <div className="flex items-center gap-4">
        <span className="bg-jj-red flex size-[52px] shrink-0 items-center justify-center rounded-xl">
          <svg
            viewBox="0 0 32 32"
            fill="currentColor"
            className="size-[30px] text-white"
            aria-hidden="true"
          >
            <path d="M12,7.5c2-0.3,4.3-0.1,6.1,0.9c0,0-0.4,1-0.4,1C16,9,14.1,8.8,12.1,8.6L12,7.5z M28.9,18.8c0,4.6-4.8,8.3-10.7,8.3S7.5,23.4,7.5,18.8c0-1,0.6-1.9,1.8-2.6c-3.6-1.1-6.2-3.8-6.2-7.1c0-2.4,4.1-4.2,9.6-4.2s9.6,1.9,9.6,4.2c0,2-1,3.8-2.5,5.1C25.1,14.5,28.9,16.4,28.9,18.8z M12.7,12.2c4.8,0,8.2-1.5,8.5-2.9c-0.2-1.3-3.8-2.7-8.5-2.7S4.4,7.9,4.2,9.2C4.5,10.7,7.8,12.2,12.7,12.2z M12.7,14.8c0.8-0.2,2.2-0.5,3.5-0.5c1.6-0.5,3-1.3,3.8-2.4c-1.7,0.9-4.4,1.5-7.4,1.5c-3,0-5.7-0.6-7.4-1.5C6.7,13.6,9.5,14.8,12.7,14.8z M26.7,21.7c-1.9,1.1-5,1.8-8.5,1.8s-6.6-0.7-8.5-1.8c1.6,2,4.9,3.3,8.5,3.3C21.8,25.1,25.1,23.8,26.7,21.7z M27.7,19.2c0-1.5-3.9-3.1-9.5-3.1c-5.6,0-9.4,1.6-9.5,3.1c0.6,1.6,4.3,3.1,9.5,3.1C23.4,22.4,27.3,20.7,27.7,19.2z M17.7,17.1v1.1c2.1,0.2,4.5,0.5,6.2,0.8l0.4-1.1C22.2,17,19.9,16.8,17.7,17.1z" />
          </svg>
        </span>
        <div>
          <p className="text-jj-red text-xs font-semibold tracking-[0.18em] uppercase">
            J&amp;J Vision
          </p>
          <h1 className="font-heading text-wg-08 mt-2 text-3xl font-semibold">
            Agentic Proactive Customer Communication
          </h1>
        </div>
      </div>
      <p className="text-wg-06 mt-3 max-w-2xl text-base">
        Two proactive communication workflows, each with a human in the loop. Choose a workflow to
        begin.
      </p>

      <div className="mt-10 grid gap-6 md:grid-cols-2">
        <WorkflowCard
          to="/ordering"
          title="Ordering Event Proactive Comms"
          description="Track every customer order through fulfilment and see which status updates have been emailed. Review the sent-email log per order and manage what each account is notified about."
          icon={
            <svg viewBox="0 0 32 32" fill="currentColor" className="h-full w-full">
              <path d="m25,23.595l-7,2.405v-1l7-2.405v1Zm0-3.023l-7,2.405v1l7-2.405v-1Zm6-12.322v17.46l-15,5.35L1,25.71V8.25l15-4.29,15,4.29Zm-8.29-.29l-11.24,3.88,4.49,1.66,11.52-4.18-4.77-1.36Zm-18.11,1.33l2.59.96,10.68-3.67-1.05-.3c-.53-.16-1.11-.16-1.64,0l-10.58,3.01Zm.39,15.72l10.05,3.59.4-14.31-4.44-1.53v8.06l-2-1.82-2,.61v-8.23l-4-1.37v12.17c0,1.27.8,2.4,1.99,2.83Zm24.01-15.06l-12.44,4.37.4,14.28,10.05-3.59c1.19-.43,1.99-1.56,1.99-2.83v-12.23Z" />
            </svg>
          }
        />
        <WorkflowCard
          to="/share"
          title="Order Share Monitoring Proactive Comms"
          description="Monitor preferred accounts against their share tiers, see where nudges were sent across their history, and review a pending reminder email before you approve it to send."
          icon={
            <svg viewBox="0 0 32 32" fill="currentColor" className="h-full w-full">
              <path d="M26.5,26.47v-20h-5V26.5h-2V15.47h-5V26.5h-2v-5.04h-5v5.04l-2,0v-23h-2v25h25v-2.03H26.5z" />
            </svg>
          }
        />
      </div>
    </>
  );
}
